import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronRight, Users, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

// Pokrytí týmu (169/C5) — přehled, u kterých zakázek kdo chybí, s možností
// doplnit všechny chybějící najednou. Otevírá se z hlavičky Zakázek.
export function ProjectTeamCoverageDialog({ open, onOpenChange, onChanged }) {
  const [cov, setCov] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = () => api.get("/projects/team-coverage").then((r) => setCov(r.data)).catch(() => setCov(null));
  useEffect(() => { if (open) { setCov(null); setExpanded({}); load(); } }, [open]);

  const s = cov?.summary;
  const projects = cov?.projects || [];

  const fillAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/projects/team/bulk", {
        mode: "add", project_ids: "all_active", user_ids: "all_eligible", dry_run: false });
      toast.success(`Doplněno ${data.pairs} přiřazení.`);
      onChanged?.();
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col" data-testid="team-coverage-dialog">
        <DialogHeader>
          <DialogTitle>Pokrytí týmu</DialogTitle>
          <DialogDescription>
            {s ? `${s.projects} zakázek · ${s.members} lidí · plně pokryté ${s.fully_covered_projects} · chybí ${s.missing_pairs} přiřazení`
               : "Načítám…"}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-auto -mx-1 px-1">
          {cov === null ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Načítám…</p>
          ) : projects.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Žádné aktivní zakázky.</p>
          ) : (
            <div className="space-y-1.5">
              {projects.map((p) => {
                const miss = p.missing || [];
                const isOpen = !!expanded[p.id];
                return (
                  <div key={p.id} className="border border-border rounded-[12px]" data-testid={`coverage-row-${p.id}`}>
                    <button onClick={() => setExpanded((e) => ({ ...e, [p.id]: !e[p.id] }))}
                      disabled={miss.length === 0}
                      className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left disabled:cursor-default">
                      {miss.length > 0
                        ? (isOpen ? <ChevronDown size={16} className="shrink-0 text-muted-foreground" />
                                  : <ChevronRight size={16} className="shrink-0 text-muted-foreground" />)
                        : <CheckCircle2 size={16} className="shrink-0 text-success" />}
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-bold truncate">{p.name}</span>
                        {p.code && <span className="block text-xs text-muted-foreground font-mono">{p.code}</span>}
                      </span>
                      <span className={`text-sm font-bold tabular-nums shrink-0 ${miss.length ? "text-amber-600 dark:text-amber-400" : "text-success"}`}>
                        {miss.length ? `chybí ${miss.length}` : "úplné"}
                      </span>
                    </button>
                    {isOpen && miss.length > 0 && (
                      <div className="px-3.5 pb-2.5 flex flex-wrap gap-1.5" data-testid={`coverage-missing-${p.id}`}>
                        {miss.map((mm) => (
                          <span key={mm.id} className="text-xs rounded-full bg-secondary px-2.5 py-1 text-muted-foreground">{mm.name}</span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {s && s.missing_pairs > 0 && (
          <div className="pt-3 border-t border-border">
            <Button onClick={fillAll} disabled={busy} className="w-full h-11 font-bold" data-testid="coverage-fill-all-btn">
              <Users size={16} /> Doplnit všechny chybějící ({s.missing_pairs})
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
